import type { Ref } from 'vue';
import { useRoute, useRouter } from '#imports';

/** Route-like shape used by scroll helper */
export interface RouteLike {
  hash?: string;
  query?: Record<string, string | undefined>;
}

export interface UsePageHelpersOptions {
  selectedFilter?: Ref<string | null>;
  pageNumber?: Ref<number>;
}

/** Return type of usePageHelpers */
export interface UsePageHelpersReturn {
  filterCallback: () => void;
  isBottomVisible: () => boolean;
}

/**
 * Scroll to element matching the route hash (client-only).
 * @param route - Route object with optional hash (e.g. from useRoute() or this.$route)
 * @returns False if scroll was performed (so caller can avoid navigation), true otherwise
 */
export function scrollToHashFromRoute(route: RouteLike): boolean {
  const hashId = route?.hash?.substring(1) ?? '';
  const elementWithHashId = import.meta.client && hashId ? document.getElementById(hashId) : null;
  if (elementWithHashId) {
    elementWithHashId.scrollIntoView();
    return true;
  }
  return false;
}

/**
 * Returns whether the bottom of the page is visible (client-only).
 */
export function isBottomVisible(): boolean {
  if (!import.meta.client) return false;
  const windowScrollY = window.scrollY;
  const documentElementClientHeight = document.documentElement.clientHeight;
  const documentElementScrollHeight = document.documentElement.scrollHeight;
  const isBottomVisible =
    documentElementClientHeight + windowScrollY >= documentElementScrollHeight / 2 ||
    documentElementScrollHeight < documentElementClientHeight;
  return isBottomVisible;
}

export function usePageHelpers(options: UsePageHelpersOptions = {}): UsePageHelpersReturn {
  const route = useRoute();
  const router = useRouter();
  const { selectedFilter = null, pageNumber = null } = options;

  function filterCallback(): void {
    if (!scrollToHashFromRoute(route as RouteLike) && selectedFilter?.value != null) {
      router.push({ query: { ...route.query, filter: selectedFilter.value } });
    }
    if (pageNumber?.value != null) pageNumber.value = 1;
  }

  return {
    filterCallback,
    isBottomVisible,
  };
}
